/**
 * The FlexLayout typed component registry (ADR-UI-003 §2). A feature never
 * touches `flexlayout-react` directly; it contributes a
 * `WorkspaceComponentDefinition`, and the registry is the only path from a
 * persisted/live `component` string to a rendered component (decision 6).
 *
 * Round A ships this machinery only — no real feature definitions are
 * registered here. Round B populates the registry with the 7 real panels.
 */
import type { LazyExoticComponent, ComponentType } from "react";
import type { PermissionCode } from "../../app/generated/shell-permission-codes";
import type { MessageKey } from "../../app/locale-store";
import { ALL_WORKSPACE_COMPONENT_IDS } from "../types";
import type { WorkspaceComponentId, WorkspaceContext, WorkspaceTabProps } from "../types";

export interface WorkspaceComponentDefinition {
  readonly id: WorkspaceComponentId;
  readonly titleKey: MessageKey;
  /**
   * Literal per-locale title, preferred over `titleKey` when present. Bridged
   * feature panels (ADR-UI-004) carry their title inline from the feature
   * descriptor rather than the shell message catalogue, so a new module needs
   * no `ShellMessages` key to appear in the workspace. Native panels keep using
   * `titleKey`; consumers fall back to it when `title` is absent.
   */
  readonly title?: { readonly de: string; readonly en: string } | undefined;
  readonly requiredPermissions: readonly PermissionCode[];
  readonly lazyComponent: LazyExoticComponent<ComponentType<WorkspaceTabProps>>;
  readonly allowedContexts: readonly WorkspaceContext[];
  readonly minimumWidth?: number | undefined;
  readonly minimumHeight?: number | undefined;
  readonly singleton?: boolean | undefined;
  /** The panel has no meaningful unfiltered view without an active incident. */
  readonly requiresIncident?: boolean | undefined;
}

export type WorkspaceComponentRegistry = ReadonlyMap<WorkspaceComponentId, WorkspaceComponentDefinition>;

const componentIdSet: ReadonlySet<string> = new Set(ALL_WORKSPACE_COMPONENT_IDS);

/** The `feature-<id>` bridge namespace (ADR-UI-004); `<id>` is a non-empty slug. */
const FEATURE_PANEL_PREFIX = "feature-";

export function isWorkspaceComponentId(value: string): value is WorkspaceComponentId {
  if (componentIdSet.has(value)) return true;
  // Feature-bridge panels use the `feature-<featureId>` namespace. The registry
  // map is still the authoritative whitelist (the factory renders
  // `UnavailableWorkspaceComponent` for any id it does not hold), so accepting
  // this shape here never lets an unregistered id mount. Crafted strings
  // (`__proto__`, path traversal, typos of real ids) do not carry the prefix
  // and remain rejected.
  return value.startsWith(FEATURE_PANEL_PREFIX) && value.length > FEATURE_PANEL_PREFIX.length;
}

/** Fails fast on a duplicate id rather than silently overwriting a registered panel. */
export function createWorkspaceComponentRegistry(
  definitions: readonly WorkspaceComponentDefinition[],
): WorkspaceComponentRegistry {
  const registry = new Map<WorkspaceComponentId, WorkspaceComponentDefinition>();
  for (const definition of definitions) {
    if (registry.has(definition.id)) {
      throw new Error(`Duplicate workspace component id: ${definition.id}`);
    }
    registry.set(definition.id, definition);
  }
  return registry;
}
